import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import AgentDashboard from "./pages/AgentDashboard";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
function ProtectedRoute({ children, isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Agent Route - Only for agents
function AgentRoute({ children, isAuthenticated }) {
  const role = localStorage.getItem("userRole");
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role !== "agent") {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Admin Route - Only for admins
function AdminRoute({ children, isAuthenticated }) {
  const role = localStorage.getItem("userRole");
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role === "agent") {
    return <Navigate to="/agent" replace />;
  }
  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Token kontrolü
    const checkAuth = async () => {
      const token = localStorage.getItem("authToken");
      const savedRole = localStorage.getItem("userRole");
      
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/verify?token=${token}`);
          if (response.data.valid) {
            setIsAuthenticated(true);
            setUserRole(savedRole || "admin");
          } else {
            clearAuth();
          }
        } catch (error) {
          clearAuth();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const clearAuth = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("username");
    localStorage.removeItem("userRole");
    localStorage.removeItem("agentId");
    localStorage.removeItem("agentName");
    setIsAuthenticated(false);
    setUserRole(null);
  };

  const handleLogin = (token, role, agentId) => {
    setIsAuthenticated(true);
    setUserRole(role || "admin");
  };

  const handleLogout = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      axios.post(`${API}/auth/logout?token=${token}`).catch(() => {});
    }
    clearAuth();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Determine redirect path based on role
  const getRedirectPath = () => {
    if (!isAuthenticated) return "/login";
    return userRole === "agent" ? "/agent" : "/";
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? <Navigate to={getRedirectPath()} replace /> : <Login onLogin={handleLogin} />
            } 
          />
          
          {/* Agent Dashboard */}
          <Route 
            path="/agent" 
            element={
              <AgentRoute isAuthenticated={isAuthenticated}>
                <AgentDashboard onLogout={handleLogout} />
              </AgentRoute>
            } 
          />
          
          {/* Admin Dashboard */}
          <Route 
            path="/" 
            element={
              <AdminRoute isAuthenticated={isAuthenticated}>
                <Dashboard onLogout={handleLogout} />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <AdminRoute isAuthenticated={isAuthenticated}>
                <Admin onLogout={handleLogout} />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/:section" 
            element={
              <AdminRoute isAuthenticated={isAuthenticated}>
                <Admin onLogout={handleLogout} />
              </AdminRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
